import { motion } from "framer-motion";
import { Sparkles, Bot, ArrowDown } from "lucide-react";
import { SectionHeading, Reveal } from "./Reveal";
import { AI_STEPS } from "../data/content";

const AiCard = ({ icon: Icon, title, desc, testid, delay }) => (
  <Reveal delay={delay} className="h-full">
    <div
      data-testid={testid}
      className="group relative h-full overflow-hidden rounded-2xl border hairline bg-card/70 p-8 transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-[0_8px_48px_var(--green-glow)]"
    >
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true" />
      <div className="relative">
        <div className="inline-flex rounded-xl border border-primary/30 bg-primary/10 p-3 text-primary">
          <Icon className="h-6 w-6" />
        </div>
        <h3 className="mt-5 font-display text-xl sm:text-2xl font-bold tracking-tight">{title}</h3>
        <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted-foreground">{desc}</p>
      </div>
    </div>
  </Reveal>
);

export const AISection = () => (
  <section id="ai" className="relative overflow-hidden border-y hairline py-24 sm:py-32" data-testid="ai-section">
    <div className="absolute inset-0 green-glow-bg opacity-80" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHeading
        index="06"
        eyebrow="Artificial Intelligence"
        title="Build software that can think, act and automate."
        sub="Farmint helps businesses move beyond traditional software with Generative AI and Agentic AI solutions."
      />
      <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <AiCard
          icon={Sparkles}
          title="Generative AI"
          desc="LLM-powered applications for content generation, intelligent search, summarization, recommendations, knowledge systems and business automation."
          testid="ai-card-generative"
          delay={0}
        />
        <AiCard
          icon={Bot}
          title="Agentic AI"
          desc="AI agents that can reason through tasks, use tools, interact with applications and execute multi-step business workflows."
          testid="ai-card-agentic"
          delay={0.12}
        />
        <Reveal delay={0.24} className="h-full">
          <div className="h-full rounded-2xl border hairline bg-background/60 p-8" data-testid="ai-workflow">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary font-semibold">Agent workflow</p>
            <div className="mt-6 flex flex-col items-stretch">
              {AI_STEPS.map((step, i) => (
                <div key={step} className="flex flex-col items-center">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.12 }}
                    className={`w-full rounded-lg border px-4 py-2 text-center font-mono text-xs uppercase tracking-[0.14em] ${
                      i === 1
                        ? "border-primary/60 bg-primary/10 text-primary"
                        : "hairline bg-card/60 text-foreground/75"
                    }`}
                  >
                    {step}
                  </motion.div>
                  {i < AI_STEPS.length - 1 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.12 + 0.1 }}
                      className="py-0.5 text-primary/60"
                    >
                      <ArrowDown className="h-3.5 w-3.5 animate-pulse-soft" style={{ animationDelay: `${i * 0.25}s` }} />
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
